import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { PageContainer } from "../components/Container/PageContainer";
import { Stack, Box, Button, Typography, Snackbar, Alert } from "@mui/material";
import { useTranslation } from "react-i18next";
import CollectionContentOfTable from "../components/CollectionContentOfTable";
import CollectionThreadContent from "../components/CollectionThreadContent";
import { IconArrowLeft } from "@tabler/icons-react";
import colorsCustom from "../designTokens/colorsCustom";
import { useUser } from "../contexts/UserContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { processThreadMediaWithProxy } from "../utils/mediaDownloader";
import { dbService } from "../firebaseInstance";
import LoopVideoBlock from "../components/video/LoopVideoBlock";
import { arrayUnion } from "firebase/firestore";

const MAG_ROOT = "magazines";
const MAG_ID = "monthlyThreads";

// Cloud Function 엔드포인트 (Section 누락 ID 자가복구용).
// 배포 전이면 호출이 실패하지만 throw 는 잡혀 silent 가시화 + 로깅만 수행.
const RESOLVE_SECTION_THREADS_URL =
	"https://us-central1-my-threads-insight.cloudfunctions.net/resolveSectionThreads";

// 누락 thread placeholder 객체를 생성. CollectionThreadContent /
// CollectionContentOfTable 가 안전하게 렌더하는 최소 필드만 노출한다.
const makePlaceholderThread = (id) => ({
	id: String(id),
	text: "이 스레드는 현재 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.",
	timestamp: new Date().toISOString(),
	hasMedia: false,
	isCarousel: false,
	mediaType: null,
	mediaUrls: [],
	permalink: null,
	username: "",
	collectionId: null,
	__placeholder: true,
});

// timestamp 가 비어/이상해도 sort 가 NaN 으로 부서지지 않게 안정화.
const safeTime = (v) => {
	const t = new Date(v).getTime();
	return Number.isFinite(t) ? t : 0;
};

const SectionPage = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { issueId, sectionIndex } = useParams();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const threadID = searchParams.get("threadID");
	const isMobile = useIsMobile("xl");
	const [section, setSection] = useState(null);
	const [threadList, setThreadList] = useState([]);
	const contentTopRef = useRef(null);
	const [shouldScrollToTitle, setShouldScrollToTitle] = useState(false);
	const [selectedThread, setSelectedThread] = useState(null);
	const [isVisible, setIsVisible] = useState(false);
	const { isLogin, profile } = useUser();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [missingCount, setMissingCount] = useState(0);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const recoveryAttemptedRef = useRef(false);

	// 섹션 비디오 URL 생성
	const sectionVideoUrl = useMemo(() => {
		if (!issueId || sectionIndex === undefined) return null;
		const sectionNumber = parseInt(sectionIndex) + 1;
		return `https://firebasestorage.googleapis.com/v0/b/my-threads-insight.firebasestorage.app/o/videos%2Fsections%2F${issueId}-s${sectionNumber}.mp4?alt=media`;
	}, [issueId, sectionIndex]);

	// 미디어 정보를 처리하는 함수 (CollectionPage와 동일)
	const processThreadWithMedia = (thread) => {
		let childrenArray = [];
		if (
			thread.children &&
			thread.children.data &&
			Array.isArray(thread.children.data)
		) {
			childrenArray = thread.children.data;
		}

		const hasMedia = !!(thread.media_url || childrenArray.length > 0);
		const mediaUrls = [];

		if (childrenArray.length > 0) {
			// 캐러셀/다중 미디어
			childrenArray.forEach((child, index) => {
				if (child.media_url) {
					mediaUrls.push({
						url: child.media_url,
						type: child.media_type,
						alt: child.alt_text || "",
					});
				}
			});
		} else if (thread.media_url) {
			// 단일 미디어
			mediaUrls.push({
				url: thread.media_url,
				type: thread.media_type,
				alt: thread.alt_text || "",
			});
		}

		const processedThread = {
			...thread,
			hasMedia,
			mediaType: thread.media_type,
			mediaUrls,
			isCarousel: !!(childrenArray.length > 1),
		};

		return processedThread;
	};

	// 원본 thread → 표시용 가공 (미디어 프록시 + preload 트리거).
	// 외부에서 자가복구 머지 시에도 동일 파이프라인을 거치도록 분리.
	const finalizeThreads = useCallback((rawThreads) => {
		const withMedia = rawThreads.map(processThreadWithMedia);
		const proxied = withMedia.map((thread) => {
			if (!thread.hasMedia) return thread;
			try {
				return processThreadMediaWithProxy(thread);
			} catch (e) {
				console.error(`❌ Failed to process proxy for thread ${thread.id}:`, e);
				return thread;
			}
		});
		// preload (fire-and-forget)
		const preloadPromises = proxied
			.filter((thread) => thread.hasMedia && thread.mediaUrls)
			.flatMap((thread) =>
				thread.mediaUrls
					.filter((media) => media.type !== "VIDEO")
					.map(
						(media) =>
							new Promise((resolve) => {
								const img = new Image();
								img.onload = () => resolve();
								img.onerror = () => resolve();
								img.src = media.url;
							})
					)
			);
		Promise.all(preloadPromises).then(() => {
			if (preloadPromises.length > 0) {
				console.log(`🎉 Images preloaded: ${preloadPromises.length}`);
			}
		});
		return proxied;
	}, []);

	// 누락 ID 를 errorLogs 에 기록 (best-effort).
	const logMissingThreads = useCallback(
		(payload) => {
			try {
				const viewerId = profile?.id ? String(profile.id) : "anonymous";
				const entry = {
					at: new Date().toISOString(),
					issueId,
					sectionIndex: String(sectionIndex),
					viewerId,
					...payload,
				};
				const ref = dbService.collection("errorLogs").doc(viewerId);
				ref
					.set(
						{
							sectionMissing: arrayUnion(entry),
						},
						{ merge: true }
					)
					.catch((e) => console.warn("[SectionPage] errorLog write failed", e));
			} catch (e) {
				console.warn("[SectionPage] errorLog skip", e);
			}
		},
		[issueId, sectionIndex, profile]
	);

	// 자가복구: 누락 ID 를 Cloud Function 으로 보내 원본 threads 에서 가져온다.
	const attemptRecovery = useCallback(
		async (missingIds) => {
			if (!Array.isArray(missingIds) || missingIds.length === 0) return;
			if (recoveryAttemptedRef.current) return;
			recoveryAttemptedRef.current = true;
			try {
				const resp = await fetch(RESOLVE_SECTION_THREADS_URL, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ issueId, missingIds }),
				});
				if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
				const data = await resp.json();
				const resolved = Array.isArray(data?.resolved) ? data.resolved : [];
				const stillMissing = Array.isArray(data?.stillMissing)
					? data.stillMissing
					: [];

				if (resolved.length > 0) {
					const recoveredRaw = resolved
						.map((r) => r?.thread)
						.filter((t) => t && t.id != null);
					const recovered = finalizeThreads(recoveredRaw);
					setThreadList((prev) => {
						// 기존 placeholder 를 동일 ID 의 실데이터로 치환, 나머지는 timestamp 기준 정렬.
						const replaced = prev.map((t) => {
							const r = recovered.find(
								(x) => String(x.id) === String(t.id)
							);
							return r || t;
						});
						const knownIds = new Set(replaced.map((t) => String(t.id)));
						const additions = recovered.filter(
							(t) => !knownIds.has(String(t.id))
						);
						const merged = [...replaced, ...additions];
						return merged.sort(
							(a, b) => safeTime(a.timestamp) - safeTime(b.timestamp)
						);
					});
				}

				setMissingCount(stillMissing.length);
				if (stillMissing.length > 0) {
					logMissingThreads({
						reason: "still-missing-after-recovery",
						missingIds: stillMissing,
						attempted: missingIds.length,
						resolved: resolved.length,
					});
				} else {
					setSnackbarOpen(false);
				}
			} catch (e) {
				console.warn("[SectionPage] resolveSectionThreads failed", e);
				logMissingThreads({
					reason: "recovery-call-failed",
					missingIds,
					error: String(e?.message || e),
				});
			}
		},
		[issueId, finalizeThreads, logMissingThreads]
	);

	// 섹션 데이터 로드
	useEffect(() => {
		recoveryAttemptedRef.current = false;
		setMissingCount(0);
		setSnackbarOpen(false);

		const loadSection = async () => {
			try {
				setLoading(true);
				setError(null);

				// 이슈 데이터 로드 (issues 우선, 없으면 issue 컬렉션)
				let issueDoc = await dbService
					.collection(MAG_ROOT)
					.doc(MAG_ID)
					.collection("issues")
					.doc(issueId)
					.get();

				if (!issueDoc.exists) {
					issueDoc = await dbService
						.collection(MAG_ROOT)
						.doc(MAG_ID)
						.collection("issue")
						.doc(issueId)
						.get();
				}

				if (!issueDoc.exists) {
					throw new Error("Issue not found");
				}

				const issueData = { id: issueDoc.id, ...issueDoc.data() };
				const sectionIdx = parseInt(sectionIndex);

				// 섹션 데이터 추출
				if (
					!issueData.sectionList ||
					!Array.isArray(issueData.sectionList) ||
					sectionIdx >= issueData.sectionList.length
				) {
					throw new Error("Section not found");
				}

				const sectionData = issueData.sectionList[sectionIdx];

				const threadIds = Array.isArray(sectionData.threadIdList)
					? sectionData.threadIdList.map((v) => String(v))
					: [];

				// items 에서 (collectionId) 평탄 추출. items 도큐먼트엔 threadId 필드가
				// 없으므로 threadId 별 역인덱스는 불가능 — 이슈에 포함된 모든
				// 컬렉션을 후보로 본다.
				const itemsSnapshot = await dbService
					.collection(MAG_ROOT)
					.doc(MAG_ID)
					.collection("issues")
					.doc(issueId)
					.collection("items")
					.get();

				const collectionIds = Array.from(
					new Set(
						itemsSnapshot.docs
							.map((d) => d.data()?.collectionId)
							.filter(Boolean)
							.map((v) => String(v))
					)
				);

				// 컬렉션 스냅샷 로드
				const collections = await Promise.all(
					collectionIds.map((colId) =>
						dbService.collection("collection").doc(colId).get()
					)
				);

				const threadMap = {};
				collections.forEach((colSnap) => {
					if (!colSnap.exists) return;
					const colData = { id: colSnap.id, ...colSnap.data() };
					const threads = Array.isArray(colData.threadList)
						? colData.threadList
						: [];
					threads.forEach((thread) => {
						if (thread?.id != null) {
							threadMap[String(thread.id)] = {
								...thread,
								collectionId: colData.id,
							};
						}
					});
				});

				// 누락 분류: silently drop 하지 않고 명시적으로 분리.
				const foundRaw = [];
				const missingIds = [];
				threadIds.forEach((id) => {
					const t = threadMap[String(id)];
					if (t) foundRaw.push(t);
					else missingIds.push(String(id));
				});

				const found = finalizeThreads(foundRaw).sort(
					(a, b) => safeTime(a.timestamp) - safeTime(b.timestamp)
				);

				// 잔존 누락은 placeholder 로 자리 유지 (TOC 카운트/순서 보존).
				const placeholders = missingIds.map(makePlaceholderThread);

				setSection({
					...sectionData,
					issueId: issueId,
					issueTitle: issueData.title || issueId,
				});
				setThreadList([...found, ...placeholders]);
				setMissingCount(missingIds.length);

				if (missingIds.length > 0) {
					console.warn("[SectionPage] missing thread ids", {
						issueId,
						sectionIndex,
						missingIds,
					});
					setSnackbarOpen(true);
					// 자가복구 시도 (비동기, 결과는 state 머지로 반영)
					attemptRecovery(missingIds);
				}
			} catch (err) {
				console.error("Failed to load section:", err);
				setError(err.message);
			} finally {
				setLoading(false);
			}
		};

		if (issueId && sectionIndex !== undefined) {
			loadSection();
		}
	}, [issueId, sectionIndex, finalizeThreads, attemptRecovery]);

	// threadID 변경시 selectedThread 업데이트
	useEffect(() => {
		if (!threadList.length) return;
		const firstReal =
			threadList.find((t) => !t.__placeholder) || threadList[0];
		if (threadID) {
			const foundThread = threadList.find((thread) => thread.id === threadID);
			setSelectedThread(foundThread || firstReal);
		} else {
			setSelectedThread(firstReal);
		}
	}, [threadID, threadList]);

	// 모바일: 목차 클릭으로 선택된 스레드 변경 시에만 스크롤 조정
	useEffect(() => {
		if (!isMobile) return;
		if (!selectedThread) return;
		if (!shouldScrollToTitle) return;
		const headerOffset = 80;
		const target = contentTopRef.current;
		if (target) {
			const top =
				target.getBoundingClientRect().top + window.scrollY - headerOffset;
			window.scrollTo({ top, behavior: "smooth" });
		}
		setShouldScrollToTitle(false);
	}, [selectedThread, isMobile, shouldScrollToTitle]);

	// 스크롤 위치 저장 및 복원 (History API 사용 - 뒤로가기 시에만 복원)
	useEffect(() => {
		const restoreScrollFromHistory = () => {
			const state = window.history.state;
			if (state?.scrollY && typeof state.scrollY === "number") {
				const attemptRestore = (delay = 0) => {
					setTimeout(() => {
						window.scrollTo(0, state.scrollY);
					}, delay);
				};

				attemptRestore(0);
				attemptRestore(100);
				attemptRestore(300);
			}
		};

		// 컴포넌트 마운트 시 스크롤 복원
		restoreScrollFromHistory();

		// 스크롤 위치를 History state에 저장
		let scrollTimeout;
		const saveScrollToHistory = () => {
			clearTimeout(scrollTimeout);
			scrollTimeout = setTimeout(() => {
				const currentState = window.history.state || {};
				const newState = { ...currentState, scrollY: window.scrollY };
				window.history.replaceState(
					newState,
					"",
					location.pathname + location.search
				);
			}, 100);
		};

		// Popstate 이벤트 리스너 (뒤로가기/앞으로가기)
		const handlePopstate = (event) => {
			if (event.state?.scrollY) {
				setTimeout(() => {
					window.scrollTo(0, event.state.scrollY);
				}, 100);
			}
		};

		window.addEventListener("scroll", saveScrollToHistory);
		window.addEventListener("beforeunload", saveScrollToHistory);
		window.addEventListener("popstate", handlePopstate);

		return () => {
			saveScrollToHistory();
			clearTimeout(scrollTimeout);
			window.removeEventListener("scroll", saveScrollToHistory);
			window.removeEventListener("beforeunload", saveScrollToHistory);
			window.removeEventListener("popstate", handlePopstate);
		};
	}, [issueId, sectionIndex, location.pathname, location.search]);

	// 섹션 데이터 로드 완료 후 추가 스크롤 복원
	useEffect(() => {
		if (section) {
			const state = window.history.state;
			if (state?.scrollY && typeof state.scrollY === "number") {
				setTimeout(() => {
					window.scrollTo(0, state.scrollY);
				}, 100);
			}
		}
	}, [section]);

	// 목차에서 선택 시에만 스크롤 트리거
	const setSelectedThreadFromToc = (...args) => {
		setShouldScrollToTitle(true);
		setSelectedThread(...args);
	};

	// 로그인하지 않은 경우 미디어 표시를 숨기기 위해 선택된 스레드의 미디어 정보를 제거
	const selectedThreadForView = useMemo(() => {
		if (!selectedThread) return null;
		if (isLogin) return selectedThread;
		return {
			...selectedThread,
			hasMedia: false,
			mediaUrls: [],
		};
	}, [selectedThread, isLogin]);

	if (loading) {
		return (
			<PageContainer>
				<Stack
					spacing={isMobile ? 6 : 12}
					sx={{
						width: "100%",
						px: isMobile ? 1 : 0,
					}}
				>
					<Typography variant="h6">로딩 중...</Typography>
				</Stack>
			</PageContainer>
		);
	}

	if (error) {
		return (
			<PageContainer>
				<Stack
					spacing={isMobile ? 6 : 12}
					sx={{
						width: "100%",
						px: isMobile ? 1 : 0,
					}}
				>
					<Typography variant="h6" color="error">
						오류: {error}
					</Typography>
					<Button
						variant="text"
						color="primary"
						size="large"
						onClick={() => navigate(-1)}
						startIcon={<IconArrowLeft size={22} />}
						sx={{
							textTransform: "none",
							p: 0,
						}}
					>
						돌아가기
					</Button>
				</Stack>
			</PageContainer>
		);
	}

	return (
		<PageContainer>
			<Snackbar
				open={snackbarOpen && missingCount > 0}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				autoHideDuration={8000}
				onClose={() => setSnackbarOpen(false)}
			>
				<Alert
					severity="warning"
					variant="filled"
					onClose={() => setSnackbarOpen(false)}
				>
					{`일부 스레드(${missingCount}개)를 불러오지 못했습니다. 잠시 후 새로고침해 주세요.`}
				</Alert>
			</Snackbar>
			{section && (
				<Stack
					spacing={isMobile ? 6 : 12}
					sx={{
						width: "100%",
						px: isMobile ? 1 : 0,
					}}
				>
					{/* 상단 백 버튼 */}
					<Box
						sx={{
							width: "100%",
							display: "flex",
							alignItems: "center",
							ml: -1,
							mr: -1,
						}}
					>
						<Button
							variant="text"
							color="primary"
							size="large"
							onClick={() => navigate(-1)}
							startIcon={<IconArrowLeft size={22} />}
							sx={{
								textTransform: "none",
								p: 0,
							}}
						>
							돌아가기
						</Button>
					</Box>

					{/* 타이틀 섹션 */}
					<Stack
						spacing={0}
						sx={{
							borderBottom: "solid 1px",
							borderColor: colorsCustom.ui.dark.subtle,
						}}
					>
						<Typography
							variant={isMobile ? "body2" : "body1"}
							sx={{
								color: colorsCustom.ui.dark.moderate,
								fontWeight: 600,
								lineHeight: 2.6,
								borderBottom: "solid 1px",
								borderColor: colorsCustom.ui.dark.subtle,
								mb: 3,
							}}
						>
							{(() => {
								const match = issueId?.match(/(\d{4})-(\d{2})/);
								if (match) {
									const [, year, month] = match;
									return `${year}년 ${parseInt(month)}월호: ${section.issueTitle}`;
								}
								return section.issueTitle;
							})()}
						</Typography>
						<Typography
							variant={isMobile ? "h5" : "h3"}
							sx={{
								fontFamily:
									'"source-han-serif-korean", "Noto Serif KR", "Noto Serif CJK KR", "Source Han Serif KR", "Apple SD Gothic Neo", "Malgun Gothic", "맑은 고딕", serif',
								fontWeight: 900,
								color: "text.primary",
								ml: -0.1,
								lineHeight: 1.6,
								wordBreak: "keep-all",
							}}
						>
							{section.title || `섹션 ${parseInt(sectionIndex) + 1}`}
						</Typography>
						{section.description && (
							<Typography
								variant={isMobile ? "body1" : "h5"}
								sx={{
									color: "text.secondary",
									lineHeight: 1.6,
								}}
							>
								"{section.description}"
							</Typography>
						)}

						{/* 섹션 썸네일 비디오 */}
						{sectionVideoUrl && (
							<Box
								sx={{
									width: "100%",
									maxWidth: isMobile ? "100%" : "256px",
									mt: 0,
									alignSelf: "flex-end",
								}}
							>
								<LoopVideoBlock
									videoUrl={sectionVideoUrl}
									aspectRatio="1 / 1"
									width="100%"
									borderRadius={0}
									isMobile={isMobile}
									enableHover={false}
									showError={false}
									playOnHover={false}
								/>
							</Box>
						)}
					</Stack>

					{/* 메인 콘텐츠 영역 */}
					{threadList.length > 0 ? (
						<Stack
							direction={isMobile ? "column" : "row"}
							spacing={0}
							sx={{
								width: "100%",
								alignItems: "flex-start",
								minHeight: isMobile ? "calc(100vh - 200px)" : "auto",
							}}
						>
							{selectedThreadForView && (
								<Box ref={contentTopRef} sx={{ width: "100%" }}>
									<CollectionThreadContent
										selectedThread={selectedThreadForView}
										setSelectedThread={setSelectedThread}
										threadList={threadList}
										isMobile={isMobile}
										collectionID={selectedThread?.collectionId}
										isVisible={isVisible}
										setIsVisible={setIsVisible}
										collection={{ threadList }}
										isMe={false}
										isLogin={isLogin}
									/>
								</Box>
							)}

							{/* 데스크톱에서만 목차 표시, 모바일에서는 오버레이로 */}
							{!isMobile && (
								<CollectionContentOfTable
									collection={{ threadList }}
									selectedThread={selectedThread}
									setSelectedThread={setSelectedThreadFromToc}
									isMobile={isMobile}
									t={t}
									isVisible={isVisible}
									setIsVisible={setIsVisible}
								/>
							)}

							{/* 모바일 오버레이 목차 */}
							{isMobile && (
								<Box sx={{ position: "relative", zIndex: 1500 }}>
									<CollectionContentOfTable
										collection={{ threadList }}
										selectedThread={selectedThread}
										setSelectedThread={setSelectedThreadFromToc}
										isMobile={isMobile}
										t={t}
										isVisible={isVisible}
										setIsVisible={setIsVisible}
									/>
								</Box>
							)}
						</Stack>
					) : (
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								minHeight: "40vh",
								textAlign: "center",
								gap: 2,
								px: 2,
							}}
						>
							<Typography
								variant="h6"
								sx={{
									fontWeight: 600,
									color: "text.primary",
								}}
							>
								스레드가 없습니다
							</Typography>
						</Box>
					)}
				</Stack>
			)}
		</PageContainer>
	);
};

export default SectionPage;
